<template>
  <div class="station-settings container">
    <ContentBlock>
      <div slot="instructions">
        <h5 class="f5">{{ $t("Multiple Stations Settings") }}</h5>
        <p class="f5">{{ $t('These are the settings for your station. If you make changes to the station during a player session, the settings will not default back once the session ends.') }}</p>
      </div>
      <div class="mainoptions">
        <General :station.sync="station" :locales="locales" @edit:station="editStation" />
      </div>
    </ContentBlock>

    <ContentBlock>
      <div slot="instructions">
        <h5 class="f5">{{ $t("Tags Filters") }}</h5>
        <p class="f5">{{ $t('Manage visible titles in the station using tags') }}</p>
      </div>
      <div class="mainoptions">
        <OperatorTagFilter :station.sync="station" :tags="tags" @edit:station="editStation" />
      </div>
    </ContentBlock>

    <BottomBar :backRouterLink="{ name: 'overview' }" :saving="saving" @save="storeStations"></BottomBar>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import StationActions from './StationActions';
import General from './General';
import OperatorTagFilter from './OperatorTagFilterList';
import ContentBlock from '@/core/shared/components/ContentBlock';
import BottomBar from '@/core/shared/components/BottomBar';
import { isEqual } from 'lodash-es';

@Component({
  props: {
    locationId: {
      type: String,
      required: true,
    },
    stationsId: {
      type: Array,
      required: true,
    },
    stationsNames: {
      type: String,
      required: true,
    },
  },
  components: {
    StationActions,
    General,
    OperatorTagFilter,
    ContentBlock,
    BottomBar,
  },
})
export default class MultipleStationsSettingsRoute extends Vue {
  stationChanges = {
    id: '0',
  };

  saving = false;

  get locales () {
    return this.$store.getters['entities/Locale/all']();
  }

  get compoundedStation () {
    const allStations = [];
    this.stationsId.forEach((id) => {
      allStations.push(this.$store.getters['entities/Station'](id));
    });

    const compoundedStation = { ...allStations[0] };
    allStations.slice(1).forEach((station) => {
      compoundedStation.ageLimit = compoundedStation.ageLimit === station.ageLimit ? compoundedStation.ageLimit : -1;
      compoundedStation.locale = compoundedStation.locale === station.locale ? compoundedStation.locale : '';
      compoundedStation.maintenanceModeEnabled = compoundedStation.maintenanceModeEnabled === station.maintenanceModeEnabled ? compoundedStation.maintenanceModeEnabled : null;
      compoundedStation.operatorTagsRequireAll = compoundedStation.operatorTagsRequireAll === station.operatorTagsRequireAll ? compoundedStation.operatorTagsRequireAll : null;

      if (compoundedStation.operatorTags.length > 0 && compoundedStation.operatorTags.length === station.operatorTags.length) {
        for (let i = 0; i < compoundedStation.operatorTags.length; i++) {
          if (compoundedStation.operatorTags[i].id !== station.operatorTags[i].id) {
            compoundedStation.operatorTags = [];
            break;
          }
        }
      } else {
        compoundedStation.operatorTags = [];
      }
    });

    compoundedStation.id = '0';
    compoundedStation.upToDate = true;
    compoundedStation.ages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

    return compoundedStation;
  }

  get station () {
    return { ...this.compoundedStation, ...this.stationChanges };
  }

  get tags () {
    const func = this.$store.getters['entities/Tag/all'];
    return func !== undefined && (typeof func === 'function') ? func() : [];
  }

  editStation (property, value) {
    const dirty = !isEqual(this.compoundedStation[property], value);
    if (dirty) this.$set(this.stationChanges, property, value);
    else this.$delete(this.stationChanges, property);
  }

  created () {
    this.$store.commit('setTitle', this.stationsNames);
    this.$store.dispatch('getLocales');
  }

  async storeStations () {
    this.saving = true;
    this.stationChanges.id = this.stationsId;
    await this.$store.dispatch('storeStations', this.stationChanges);
    this.$store.dispatch('getLocationStations', this.locationId).then(() => {
      this.saving = false;
      this.$router.push({ name: 'overview' });
    });
  }

  beforeDestroy () {
    this.$store.dispatch('setLocationHeader');
  }
}
</script>

<style scoped lang="postcss">
@import 'core/shared/styles';

.station-settings {
  padding-top: var(--spacingMd);

  & .mainoptions {
    display: grid;
    grid-gap: var(--spacingMd);
  }

  & .update {
    background-color: var(--colorErrorFlat);
    position: relative;
    top: -var(--spacingSm);
    left: -var(--spacingSm);
    width: calc(100% + calc(var(--spacingSm) * 2));
    padding: var(--spacingSm);
    text-align: center;
    border-radius: var(--radius) var(--radius) 0 0;

    @media (--tablet) {
      display: flex;
      top: -var(--spacingLg);
      left: -var(--spacingLg);
      width: calc(100% + calc(var(--spacingLg) * 2));
      padding: var(--spacingLg);
      text-align: left;
      align-items: center;

      & > * {
        margin-right: var(--spacingMd);

        &:last-child {
          margin-right: 0;
        }
      }
    }

    & .actions {
      text-align: center;
    }

    & svg {
      fill: var(--colorWhite);
      width: 5rem;
      height: 5rem;
    }

    & h3 {
      color: var(--colorWhite);
    }

    & p {
      color: var(--colorWhite);
    }
  }
}
</style>
